import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';

type UseSyncRegistryCurrencyToFormArgs = Readonly<{
  isEditDialog?: boolean;
  isNewCashFund?: boolean;
  registryCurrencyCode: Maybe<string>;
  mountedCurrencyCodeRef: MutableRefObject<string>;
  onCurrencyChange: (currencySymbol: string, currencyCode: string) => void;
}>;

/**
 * Syncs the registry currency into the cash fund form when it changes after mount
 * (e.g. after Stripe payout onboarding sets a non-USD currency for a catalog fund).
 *
 * Runs for new funds — custom adds (`isEditDialog === false`) and catalog adds
 * (`isEditDialog === true` + `isNewCashFund === true`) — but bails for existing-fund
 * edits, whose currency is locked. The mount ref guards against redundant updates so
 * the form only changes when the registry currency actually differs from the last value.
 */
export const useSyncRegistryCurrencyToForm = ({
  isEditDialog,
  isNewCashFund,
  registryCurrencyCode,
  mountedCurrencyCodeRef,
  onCurrencyChange,
}: UseSyncRegistryCurrencyToFormArgs) => {
  const onCurrencyChangeRef = useRef(onCurrencyChange);
  onCurrencyChangeRef.current = onCurrencyChange;

  useEffect(() => {
    if (isEditDialog && !isNewCashFund) return;
    if (!registryCurrencyCode || registryCurrencyCode === mountedCurrencyCodeRef.current) return;
    mountedCurrencyCodeRef.current = registryCurrencyCode;
    onCurrencyChangeRef.current(getCurrencySymbolFromCurrencyCode(registryCurrencyCode)!, registryCurrencyCode);
  }, [registryCurrencyCode, isEditDialog, isNewCashFund, mountedCurrencyCodeRef]);
};
